import React, { useState, useEffect } from 'react'
import { Link, Redirect, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { Helmet } from 'react-helmet'
import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { CREATE, GET_ONE, useClient, getSchemaFromResource } from '@peracto/client'
import { MODE_ADD } from '../ProductMenuItemRelationsForm'
import { getProductName } from '../../Products/util'

const ProductAdd = ({ ProductMenuItemRelationsForm }) => {
    const { client, getResource } = useClient()
    const { id } = useParams()

    const [redirect, setRedirect] = useState()
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [parentProduct, setParentProduct] = useState()
    const [loading, setLoading] = useState(true)

    const schema = getSchemaFromResource(getResource('menu-item-relations')).shape({
        menuItems: yup.string().required(),
        label: yup.string().required(),
    })

    const fetchProductOptions = async () => {
        setLoading(true)
        try {
            const { data: productData, response } = await client(GET_ONE, 'products', {
                id: `/products/${id}`,
            })

            setParentProduct({ sku: productData.sku, name: getProductName(productData) })
            setLoading(false)

            if (response.status === 404) {
                setRedirect(`/products/${id}/menu-item-relations`)
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect(`/products/${id}/menu-item-relations`)
        }
    }

    const onSubmit = async (data, actions) => {
        try {
            const response = await client(CREATE, 'product-menu-item-relations', {
                data: {
                    ...data,
                    displayOrder: typeof data.displayOrder === 'number' ? data.displayOrder : 0,
                },
            })

            if (response?.data?.originId) {
                setRedirect(`/products/${id}/menu-item-relations/${response.data.originId}`)
            } else {
                setRedirect(`/products/${id}/menu-item-relations`)
            }

            toast.success('Menu Item Relation successfully added!')
            actions.setSubmitting(false)
        } catch (e) {
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            actions.setValues(data)
            actions.setSubmitting(false)
        }
    }

    useEffect(() => {
        fetchProductOptions()
    }, [])

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url={`/products`}
                message="The product you're looking for could not be found"
                buttonLabel="Go to Products"
            />
        )
    }

    if (redirect) {
        return <Redirect to={redirect} />
    }

    return (
        <>
            <Helmet>
                <title>Product Menu Item Relation | Add | Peracto</title>
            </Helmet>

            {loading ? (
                <div className="form-container">
                    <div className="card">
                        <div className="card-body">Loading...</div>
                    </div>
                </div>
            ) : (
                <div className="form-container">
                    <Heading
                        name={`Add Product Menu Item Relation for ${parentProduct.name} (${parentProduct.sku})`}
                    >
                        <div className="!grow !flex !items-center !justify-end">
                            <Link
                                className="btn btn-outline-primary !whitespace-nowrap"
                                to={`/products/${id}/menu-item-relations`}
                                data-testid="back-to-product-menu-item-relations"
                            >
                                <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                                Back to Product Menu Item Relations
                            </Link>
                        </div>
                    </Heading>
                    <ProductMenuItemRelationsForm
                        onSubmit={onSubmit}
                        mode={MODE_ADD}
                        schema={schema}
                    />
                </div>
            )}
        </>
    )
}

export default ProductAdd
